/* =================================================================
   br-frontrowmd-quant — consent-aware FrontrowMD quantitative widget.

   FrontrowMD's official loader creates persistent visitor/session IDs and
   emits widget analytics. Policy (Daria, 2026-09-01): the widget is core
   social-proof content and loads BY DEFAULT — including when Shopify
   Customer Privacy is missing, slow, or undecided — and is blocked only by
   an EXPLICIT Analytics denial (banner rejection or a GPC signal, which
   several US states legally require honoring). The previous fail-closed
   posture hid the widget from consenting visitors whenever the privacy
   runtime failed to surface (no retry existed); measured live 2026-09-01.
   Withdrawal handling (2026-09-07): an explicit denial after load tears the
   widget down in place. The theme never reloads the page.
   Explicit means explicit: a GDPR visitor who has not answered the banner
   is undecided, not refused, and sees the widget (see analyticsPermission).

   Runtime race (2026-09-07): "unavailable" and "not loaded yet" are not the
   same thing. Shopify's consent-tracking-api arrives asynchronously, and on
   a warm cache this script executes before it (measured on live: 28 ms
   apart, order flips per load). Starting the vendor on that first null
   read meant an EU visitor got the tracking vendor before any decision, and
   the withdrawal reload fired when the runtime caught up with a stored
   denial. Now: when the runtime is loadable but absent, the controller
   waits for it, bounded by RUNTIME_WAIT_MS, and fail-opens only if it never
   surfaces. A denial that lands while a load is in flight tears the load
   down instead of letting the iframe finish behind the fallback.
   It owns one quantitative iframe and one official product-specific script,
   remains idempotent across Theme Editor reloads, and keeps the neutral
   fallback visible until the iframe reports a successful resize.
   ================================================================= */

(function installFrontrowmdQuant(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  if (!root.brFrontrowmdQuant) {
    root.brFrontrowmdQuant = api.createController(root);
    root.brFrontrowmdQuant.start();
  } else {
    root.brFrontrowmdQuant.initialize(root.document);
  }
})(typeof window === 'undefined' ? globalThis : window, function createFrontrowmdQuantApi() {
  'use strict';

  const FRONTROW_ORIGIN = 'https://app.thefrontrowhealth.com';
  const MOUNT_SELECTOR = '[data-br-frontrowmd-quant]';
  const FALLBACK_SELECTOR = '[data-br-frontrowmd-fallback]';
  const IFRAME_ID = 'iframe-frontrow-quant';
  const SCRIPT_MARKER = 'brFrontrowmdQuantScript';
  const LOAD_TIMEOUT_MS = 12000;
  const RUNTIME_WAIT_MS = 3000;
  const MAX_RESIZE_HEIGHT = 500;

  function createController(root) {
    const document = root.document;
    const states = new WeakMap();
    const activeStates = new Set();
    let started = false;
    let privacyLoadRequested = false;
    let privacyRuntimeSettled = false;
    let vendorRuntimeStarted = false;
    // Within-load guard. The session key stops the NEXT page reloading; this
    // stops a second consent event on THIS page calling reload again before
    // the navigation commits. The pre-2026-09-07 code had only this one, which
    // is precisely why it looped across loads.
    let reloadRequested = false;

    function findMounts(scope) {
      if (!scope) return [];
      if (scope.matches?.(MOUNT_SELECTOR)) return [scope];
      return Array.from(scope.querySelectorAll?.(MOUNT_SELECTOR) || []);
    }

    function globalPrivacyControl() {
      const navigator = root.navigator;
      const signal = navigator && navigator.globalPrivacyControl;
      return signal === true || signal === 1;
    }

    // Session-scoped memory of an explicit denial.
    //
    // WHY THIS EXISTS. FrontrowMD's loader injects iframes of its own outside
    // this controller's mount — measured on live 2026-09-07 after a withdrawal
    // handled purely in place: widgets/storage_setter, widgets/modal and
    // widgets/v1/provider_profiles all survived, the first being the visitor
    // identity setter. Removing our iframe and our script tag does not undo
    // what the loader already executed. A reload is still the only reliable way
    // to clear that runtime, which is what the pre-2026-09-07 code said.
    //
    // The reload was removed because it looped: fail-open started the vendor
    // before Shopify reported the stored denial, the denial arrived, the page
    // reloaded, and a consent app that re-broadcasts the decision on every load
    // made that endless (measured on live: 15 navigations in 15 s on iPhone,
    // 11 on desktop with the page left unscrollable). The loop was never the
    // reload itself — it was reloading into a page that fail-opens again.
    //
    // So: remember the denial for the session, and the reloaded page starts
    // nothing. vendorRuntimeStarted then stays false, so nothing asks for a
    // second reload. The loop is closed by construction rather than by a
    // once-per-load flag, which is what the old code had and why it failed.
    const DENIED_KEY = 'br-frontrowmd-analytics-denied';

    function denialRemembered() {
      try {
        return root.sessionStorage && root.sessionStorage.getItem(DENIED_KEY) === '1';
      } catch (_error) {
        return false;
      }
    }

    function rememberDenial() {
      try {
        if (root.sessionStorage) root.sessionStorage.setItem(DENIED_KEY, '1');
      } catch (_error) {
        /* private mode or blocked storage: the runtime wait still covers us */
      }
    }

    function forgetDenial() {
      try {
        if (root.sessionStorage) root.sessionStorage.removeItem(DENIED_KEY);
      } catch (_error) {
        /* nothing to do */
      }
    }

    // Policy (Daria, 2026-09-01, reaffirmed 2026-09-07): the widget is
    // blocked ONLY by an explicit refusal. In a GDPR region Shopify reports
    // analyticsProcessingAllowed() === false for a visitor who has not
    // answered the banner yet — that is "undecided", not "refused", and it
    // must not hide the widget. Explicit refusal is the visitor's recorded
    // "no" (currentVisitorConsent) or a Global Privacy Control signal.
    //   true  — allowed
    //   false — explicitly refused
    //   null  — undecided or runtime unavailable (fail-open)
    // What Shopify currently reports, ignoring anything we remembered. Only
    // the consent handler uses this, to notice a later "yes" — the effective
    // reading below can never return true once a denial is remembered, so
    // reading it there would make re-consent impossible for the whole session.
    function reportedAnalyticsPermission() {
      if (globalPrivacyControl()) return false;

      const customerPrivacy = root.Shopify && root.Shopify.customerPrivacy;
      if (!customerPrivacy || typeof customerPrivacy.analyticsProcessingAllowed !== 'function') {
        return null;
      }

      try {
        if (typeof customerPrivacy.currentVisitorConsent === 'function') {
          const consent = customerPrivacy.currentVisitorConsent();
          const decision = consent && consent.analytics;
          if (decision === 'no') return false;
          if (decision === 'yes') return true;
          // '' = no decision recorded. Allowed where no consent is required
          // (the API says so); undecided where it is.
          return customerPrivacy.analyticsProcessingAllowed() === true ? true : null;
        }

        const allowed = customerPrivacy.analyticsProcessingAllowed();
        return allowed === true ? true : allowed === false ? false : null;
      } catch (_error) {
        return null;
      }
    }

    // The reading everything else acts on: a denial recorded this session
    // outranks a runtime that has not caught up yet. Without it the reloaded
    // page fail-opens on the first null read and starts the vendor again
    // before the answer arrives — which is the loop.
    function analyticsPermission() {
      if (denialRemembered()) return false;
      return reportedAnalyticsPermission();
    }

    function privacyRuntimePresent() {
      const customerPrivacy = root.Shopify && root.Shopify.customerPrivacy;
      return Boolean(
        customerPrivacy && typeof customerPrivacy.analyticsProcessingAllowed === 'function'
      );
    }

    function privacyRuntimeLoadable() {
      const shopify = root.Shopify;
      return Boolean(shopify && typeof shopify.loadFeatures === 'function');
    }

    // True while the official runtime has been asked for but has not answered:
    // the one window in which "null" means "not yet", not "unavailable".
    function privacyRuntimePending() {
      return privacyLoadRequested && !privacyRuntimeSettled && !privacyRuntimePresent();
    }

    function settlePrivacyRuntime() {
      privacyRuntimeSettled = true;
      initialize(document);
    }

    function requestPrivacyRuntime() {
      if (privacyRuntimePresent() || privacyLoadRequested) return;
      if (!privacyRuntimeLoadable()) return;

      privacyLoadRequested = true;

      try {
        root.Shopify.loadFeatures(
          [
            {
              name: 'consent-tracking-api',
              version: '0.1',
            },
          ],
          settlePrivacyRuntime
        );
      } catch (_error) {
        settlePrivacyRuntime();
      }
    }

    function setStatus(state, status, busy) {
      state.status = status;
      state.mount.dataset.frontrowmdState = status;
      state.mount.setAttribute('aria-busy', String(busy));
    }

    function showFallback(state) {
      if (state.fallback) state.fallback.hidden = false;
    }

    function hideFallback(state) {
      if (state.fallback) state.fallback.hidden = true;
    }

    function clearFailureTimer(state) {
      if (state.failureTimer == null) return;
      root.clearTimeout(state.failureTimer);
      state.failureTimer = null;
    }

    function clearRuntimeWaitTimer(state) {
      if (state.runtimeWaitTimer == null) return;
      root.clearTimeout(state.runtimeWaitTimer);
      state.runtimeWaitTimer = null;
    }

    // Bounded wait for the official runtime. If it never surfaces, the
    // fail-open policy applies: load. If it surfaces first, settlePrivacyRuntime
    // re-enters connect() and this timer is cleared there.
    function awaitPrivacyRuntime(state) {
      if (state.runtimeWaitTimer != null) return;

      showFallback(state);
      setStatus(state, 'awaiting-runtime', true);
      state.runtimeWaitTimer = root.setTimeout(() => {
        state.runtimeWaitTimer = null;
        if (state.status !== 'awaiting-runtime') return;
        privacyRuntimeSettled = true;
        connect(state.mount);
      }, RUNTIME_WAIT_MS);
    }

    // A denial that arrives while resources are in flight (the runtime caught
    // up with a stored decision). Nothing has been proven to execute yet, so
    // this tears down without the withdrawal reload.
    function abandonForDenial(state) {
      clearFailureTimer(state);
      clearRuntimeWaitTimer(state);
      removeIframe(state);
      state.observedIframe = null;
      if (state.script && !state.script.dataset?.brFrontrowmdExecuted) {
        state.script.remove();
        state.script = null;
        state.observedScript = null;
      }
      showFallback(state);
      setStatus(state, 'consent-denied', false);
    }

    function removeIframe(state) {
      releaseReadyHeight(state);
      if (!state.iframe) return;
      state.iframe.remove();
      state.iframe = null;
    }

    function fail(state, status) {
      clearFailureTimer(state);
      removeIframe(state);
      showFallback(state);
      setStatus(state, status, false);
    }

    function haltForConsentWithdrawal(state, status) {
      clearFailureTimer(state);
      clearRuntimeWaitTimer(state);
      removeIframe(state);

      if (state.script) state.script.remove();
      state.script = null;
      state.observedIframe = null;
      state.observedScript = null;

      showFallback(state);
      setStatus(state, status, false);
    }

    function parseConfig(mount) {
      const productId = (mount.dataset.frontrowmdProductId || '').trim();
      if (!/^\d+$/.test(productId)) return null;

      try {
        const scriptUrl = new root.URL(mount.dataset.frontrowmdScriptUrl);
        const iframeUrl = new root.URL(mount.dataset.frontrowmdIframeUrl);
        const valid = [scriptUrl, iframeUrl].every(
          (url) =>
            url.origin === FRONTROW_ORIGIN &&
            url.searchParams.get('presentation_type') === 'quant' &&
            url.searchParams.get('product_id') === productId
        );

        if (
          !valid ||
          scriptUrl.pathname !== '/api/widgets/script' ||
          iframeUrl.pathname !== '/api/widgets'
        ) {
          return null;
        }

        return {
          productId,
          scriptUrl: scriptUrl.toString(),
          iframeUrl: iframeUrl.toString(),
          iframeTitle: mount.dataset.frontrowmdIframeTitle || 'FrontrowMD Clinicians Choice',
        };
      } catch (_error) {
        return null;
      }
    }

    function findScript(scriptUrl) {
      return Array.from(document.scripts || []).find(
        (script) => script.src === scriptUrl || script.getAttribute('src') === scriptUrl
      );
    }

    function attachIframeError(state, iframe) {
      if (state.observedIframe === iframe) return;
      state.observedIframe = iframe;
      iframe.addEventListener('error', () => handleNetworkFailure(state, iframe), { once: true });
    }

    function ensureIframe(state) {
      let iframe = document.getElementById(IFRAME_ID);

      if (iframe) {
        const currentUrl = iframe.src || iframe.getAttribute('src');
        if (currentUrl !== state.config.iframeUrl || iframe.parentNode !== state.mount) {
          throw new Error('frontrowmd-iframe-conflict');
        }
      } else {
        iframe = document.createElement('iframe');
        iframe.id = IFRAME_ID;
        iframe.className = 'br-hero-home-c__frontrow-frame iframe-frontrow';
        iframe.title = state.config.iframeTitle;
        iframe.frameBorder = '0';
        iframe.setAttribute('loading', 'eager');
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        iframe.src = state.config.iframeUrl;
        state.mount.appendChild(iframe);
      }

      state.iframe = iframe;
      attachIframeError(state, iframe);
      return iframe;
    }

    function attachScriptError(state, script) {
      if (state.observedScript === script) return;
      state.observedScript = script;
      script.addEventListener('error', () => handleNetworkFailure(state, script), { once: true });
    }

    function ensureScript(state) {
      let script = findScript(state.config.scriptUrl);

      if (!script) {
        const managedScript = Array.from(document.scripts || []).find(
          (candidate) => candidate.dataset[SCRIPT_MARKER] === 'true'
        );
        if (managedScript) throw new Error('frontrowmd-script-conflict');

        script = document.createElement('script');
        script.async = true;
        script.type = 'text/javascript';
        script.dataset[SCRIPT_MARKER] = 'true';
        script.src = state.config.scriptUrl;
        script.addEventListener(
          'load',
          () => {
            script.dataset.brFrontrowmdExecuted = 'true';
          },
          { once: true }
        );
        document.head.appendChild(script);
      }

      state.script = script;
      vendorRuntimeStarted = true;
      attachScriptError(state, script);
      return script;
    }

    function scheduleFailure(state) {
      clearFailureTimer(state);
      state.failureTimer = root.setTimeout(() => fail(state, 'timeout'), LOAD_TIMEOUT_MS);
    }

    function beginAttempt(state) {
      if (state.status === 'ready' || analyticsPermission() === false) return;

      showFallback(state);
      setStatus(state, 'loading', true);

      try {
        // The official loader looks up this exact iframe ID during execution,
        // so the iframe must exist before the async script can finish loading.
        ensureIframe(state);
        ensureScript(state);
        scheduleFailure(state);
      } catch (_error) {
        fail(state, 'error');
      }
    }

    function handleNetworkFailure(state, resource) {
      if (state.status !== 'loading') return;
      if (resource !== state.iframe && resource !== state.script) return;

      clearFailureTimer(state);

      if (resource === state.script) {
        state.script.remove();
        state.script = null;
        state.observedScript = null;
      }
      removeIframe(state);
      state.observedIframe = null;

      if (state.retryCount < 1) {
        state.retryCount += 1;
        setStatus(state, 'retrying', true);
        root.setTimeout(() => beginAttempt(state), 0);
        return;
      }

      showFallback(state);
      setStatus(state, 'error', false);
    }

    // The vendor loader rewrites the iframe height itself on every
    // RESIZE_HEIGHT it sees — including the zeros the widget posts while it
    // retries its handshake. Once the widget has proven a real height, keep
    // that space: reserve it on the mount and put back any rewrite that
    // shrinks the frame, so the hero never bounces under the visitor. The
    // expanded PDP updates this reserve on each valid resize, allowing settled
    // content to shrink without keeping the widget's initial empty space.
    function holdReadyHeight(state) {
      if (state.heightObserver || typeof root.MutationObserver !== 'function' || !state.iframe)
        return;

      state.heightObserver = new root.MutationObserver(() => {
        if (state.status !== 'ready' || !state.iframe) return;
        const current = parseFloat(state.iframe.style.height);
        if (!Number.isFinite(current) || current < state.readyHeight) {
          state.iframe.style.height = `${state.readyHeight}px`;
        }
      });
      state.heightObserver.observe(state.iframe, { attributes: true, attributeFilter: ['style'] });
    }

    function releaseReadyHeight(state) {
      if (state.heightObserver) {
        state.heightObserver.disconnect();
        state.heightObserver = null;
      }
      state.readyHeight = 0;
      if (state.mount.style) state.mount.style.minHeight = '';
    }

    function markReady(state, height) {
      const responsive = state.mount.dataset.frontrowmdResize === 'responsive';
      if (
        !state.iframe ||
        (state.status !== 'loading' && !(responsive && state.status === 'ready'))
      )
        return;

      clearFailureTimer(state);
      state.readyHeight = Math.ceil(height);
      state.iframe.style.height = `${state.readyHeight}px`;
      state.iframe.dataset.brFrontrowmdReady = 'true';
      if (state.mount.style) state.mount.style.minHeight = `${state.readyHeight}px`;
      hideFallback(state);
      setStatus(state, 'ready', false);
      holdReadyHeight(state);
    }

    function connect(mount) {
      let state = states.get(mount);
      if (!state) {
        state = {
          mount,
          fallback: mount.querySelector(FALLBACK_SELECTOR),
          config: null,
          status: 'idle',
          retryCount: 0,
          failureTimer: null,
          runtimeWaitTimer: null,
          heightObserver: null,
          readyHeight: 0,
          iframe: null,
          script: null,
          observedIframe: null,
          observedScript: null,
        };
        states.set(mount, state);
        activeStates.add(state);
      }

      // Fail-open: only an explicit denial blocks. An unavailable or undecided
      // privacy runtime (null) loads the widget. The one exception is a runtime
      // that has been requested and simply has not answered yet — that wait is
      // bounded (RUNTIME_WAIT_MS) and re-enters here when it settles.
      const permission = analyticsPermission();
      if (permission === false) {
        if (state.status === 'loading' || state.status === 'awaiting-runtime') {
          abandonForDenial(state);
          return;
        }
        showFallback(state);
        if (state.status === 'idle' || state.status === 'blocked') {
          setStatus(state, 'consent-denied', false);
        }
        return;
      }

      if (
        state.status === 'ready' ||
        state.status === 'loading' ||
        state.status === 'error' ||
        state.status === 'timeout'
      ) {
        return;
      }

      if (permission === null && privacyRuntimePending()) {
        awaitPrivacyRuntime(state);
        return;
      }

      clearRuntimeWaitTimer(state);

      state.config = parseConfig(mount);
      if (!state.config) {
        fail(state, 'error');
        return;
      }

      beginAttempt(state);
    }

    function initialize(scope = document) {
      findMounts(scope).forEach(connect);
    }

    function handleConsentChange() {
      const permission = reportedAnalyticsPermission();

      if (permission === true) {
        // Re-consent: forget the denial so the widget may start again.
        forgetDenial();
        initialize(document);
        return;
      }

      if (permission === false && vendorRuntimeStarted && !reloadRequested) {
        reloadRequested = true;
        rememberDenial();
        activeStates.forEach((state) => haltForConsentWithdrawal(state, 'consent-denied'));

        // One reload, and only when the vendor actually started. The reloaded
        // page reads the remembered denial, starts nothing, and therefore never
        // reaches this branch again. See DENIED_KEY above for why tearing down
        // in place is not sufficient.
        if (root.location && typeof root.location.reload === 'function') {
          root.location.reload();
        }
        return;
      }

      if (permission === false) {
        rememberDenial();
        activeStates.forEach((state) => haltForConsentWithdrawal(state, 'consent-denied'));
        return;
      }

      initialize(document);
    }

    function handleMessage(event) {
      if (event.origin !== FRONTROW_ORIGIN) return;
      if (!event.data || event.data.name !== 'RESIZE_HEIGHT') return;

      const height = Number(event.data.value);
      if (!Number.isFinite(height) || height <= 0 || height > MAX_RESIZE_HEIGHT) return;

      for (const state of activeStates) {
        if (state.iframe && event.source === state.iframe.contentWindow) {
          markReady(state, height);
          return;
        }
      }
    }

    function disconnect(scope) {
      findMounts(scope).forEach((mount) => {
        const state = states.get(mount);
        if (!state) return;
        clearFailureTimer(state);
        clearRuntimeWaitTimer(state);
        state.status = 'unloaded';
        state.iframe = null;
        state.script = null;
        state.observedIframe = null;
        state.observedScript = null;
        activeStates.delete(state);
        states.delete(mount);
      });
    }

    function start() {
      if (started) {
        initialize(document);
        return;
      }

      started = true;
      root.addEventListener('message', handleMessage);
      document.addEventListener('visitorConsentCollected', handleConsentChange);
      document.addEventListener('shopify:section:load', (event) => initialize(event.target));
      document.addEventListener('shopify:section:unload', (event) => disconnect(event.target));
      requestPrivacyRuntime();
      initialize(document);
    }

    return {
      analyticsPermission,
      initialize,
      start,
      stateFor(mount) {
        return states.get(mount)?.status || null;
      },
    };
  }

  return { createController };
});
